/**
 * Bản đồ các Vườn của một Hộ, chỉ xem: kiểm tra nhanh mảnh đang nằm ở đâu mà không phải mở màn vẽ.
 *
 * Chỉ dựng khi có ít nhất một Vườn đã vẽ ranh giới. Hộ chưa vẽ thì không tải thư viện bản đồ và không
 * tốn lượt tải ảnh nền (Mapbox tính theo lượt), trong khi đa số hồ sơ đang ở trạng thái này.
 *
 * Leaflet nạp động như màn vẽ, để các màn khác không phải tải.
 */
import { describeBoundary } from '@farmer/audit-rules';
import { basemapFor, describeTileFailure, plotMapItems } from './boundary-editor.js';
import { buildHash, esc } from './util.js';

/**
 * Ranh giới khó thấy trên ảnh vệ tinh nếu dùng màu tối: cùng màu với màn vẽ. Chỉ vẽ đường, không tô, để
 * nhìn rõ ảnh bên trong mảnh. `fillOpacity: 0` chứ không phải `fill: false`: tắt hẳn phần tô thì bấm vào
 * giữa mảnh không còn mở được thông tin, chỉ bấm trúng đường viền mới được.
 */
const BOUNDARY_STYLE = { color: '#f5c542', weight: 3, fillOpacity: 0 };

/**
 * Hỏi lại đúng URL ô ảnh vừa lỗi để lấy mã HTTP và lời Mapbox — `<img>` của Leaflet chỉ báo
 * "lỗi", không cho biết vì sao. Mapbox trả CORS cả với phản hồi lỗi; `fetch` ném lỗi nghĩa là
 * không tới được máy chủ (status 0).
 */
async function probeTile(url) {
  try {
    const res = await fetch(url, { cache: 'no-store' });
    let message = null;
    if (!res.ok) message = (await res.json().catch(() => null))?.message ?? null;
    return { status: res.status, message };
  } catch {
    return { status: 0, message: null };
  }
}

/**
 * Ô ảnh Mapbox đầu tiên lỗi → hỏi lý do, hiện thông báo chi tiết (Doc/09 mục 8.9, quyết định 10).
 * Lỗi do token thì đổi sang OpenStreetMap để vẫn xem và vẽ tiếp được. Chỉ hỏi một lần: token hỏng thì
 * mọi ô đều lỗi, không cần gọi lại cho từng ô. Ô lỗi mà hỏi lại tải được → lỗi thoáng qua, bỏ qua.
 */
export function watchMapboxTiles(map, layer, L, bannerEl, token) {
  let handled = false;
  layer.on('tileerror', async (e) => {
    if (handled) return;
    handled = true;
    const { status, message } = await probeTile(e.tile.src);
    const failure = describeTileFailure(status, message, token);
    if (!bannerEl.isConnected) return; // đã rời màn này
    if (!failure) {
      handled = false;
      return;
    }
    bannerEl.innerHTML = `<span class="ms">error</span><span><b>${esc(failure.title)}.</b> ${esc(failure.detail)}</span>`;
    bannerEl.hidden = false;
    if (failure.tokenProblem) {
      map.removeLayer(layer);
      const osm = basemapFor('');
      L.tileLayer(osm.url, osm.options).addTo(map).bringToBack();
    }
  });
}

function popupHtml(plot) {
  const code = plot.plotCode || 'Vườn chưa có mã';
  const boundary = describeBoundary(plot.boundaryGeojson);
  return `<div class="plot-map-popup">
    <b>${esc(code)}</b>
    <div>Khai báo ${esc(plot.areaHa)} ha${boundary ? ` · ranh giới ${esc(boundary)}` : ''}</div>
    <a href="${buildHash(`/plots/${plot.id}/boundary`)}">${boundary ? 'Sửa ranh giới' : 'Vẽ ranh giới'}</a>
  </div>`;
}

/**
 * @param {HTMLElement} host - khung trống, ẩn sẵn; tự hiện khi có gì để vẽ
 * @param {Array<object>} plots
 * @returns {Promise<{focus: (plotId: string) => void, destroy: () => void} | null>} `null` khi không dựng
 */
export async function mountPlotsMap(host, plots) {
  const { polygons, points } = plotMapItems(plots);
  if (!polygons.length) return null;

  host.hidden = false;
  host.innerHTML = `
    <div class="status-banner tone-error" data-role="error" role="alert" hidden></div>
    <div class="plot-map" data-role="map" role="region" aria-label="Bản đồ các vườn của hộ"></div>
    ${
      points.length
        ? `<p class="hint-text plot-map-note">Ghim là vườn chưa vẽ ranh giới, đặt theo toạ độ hồ sơ.</p>`
        : ''
    }`;
  const offlineNote = () => {
    host.innerHTML = `<p class="hint-text plot-map-note"><span class="ms ms-sm">cloud_off</span> Bản đồ cần có mạng. ${esc(
      polygons.map(({ plot }) => `${plot.plotCode || 'Vườn'}: ${describeBoundary(plot.boundaryGeojson)}`).join(' · '),
    )}</p>`;
  };
  if (navigator.onLine === false) {
    offlineNote();
    return null;
  }

  let L;
  try {
    [{ default: L }] = await Promise.all([import('leaflet'), import('leaflet/dist/leaflet.css')]);
  } catch (err) {
    console.warn('[plot-map] không tải được thư viện bản đồ', err);
    if (host.isConnected) offlineNote();
    return null;
  }
  if (!host.isConnected) return null; // đã rời màn này trong lúc chờ tải

  const mapEl = host.querySelector('[data-role="map"]');
  // Xem nhanh giữa trang: lăn chuột và vuốt trên điện thoại phải cuộn trang, không bị bản đồ giữ lại.
  const map = L.map(mapEl, { scrollWheelZoom: false, dragging: !L.Browser.mobile });
  const basemap = basemapFor(import.meta.env.VITE_MAPBOX_TOKEN);
  const baseLayer = L.tileLayer(basemap.url, basemap.options).addTo(map);
  if (basemap.kind === 'mapbox') {
    watchMapboxTiles(map, baseLayer, L, host.querySelector('[data-role="error"]'), import.meta.env.VITE_MAPBOX_TOKEN);
  }

  const layers = new Map();
  for (const { plot, latlngs } of polygons) {
    layers.set(plot.id, L.polygon(latlngs, BOUNDARY_STYLE).addTo(map).bindPopup(popupHtml(plot)));
  }
  for (const { plot, latlng } of points) {
    L.circleMarker(latlng, { radius: 6, color: BOUNDARY_STYLE.color, weight: 2, fillOpacity: 0.6 })
      .addTo(map)
      .bindPopup(popupHtml(plot));
  }

  // Khung nhìn theo các mảnh đã vẽ. Ghim chỉ là phụ: một toạ độ nhập sai ở xa không được kéo bản đồ ra
  // tầm nhìn cả tỉnh, làm chính mảnh cần xem nhỏ lại thành một chấm.
  const fit = (bounds) => map.fitBounds(bounds, { padding: [32, 32], maxZoom: 19 });
  fit(L.featureGroup([...layers.values()]).getBounds());
  // Bản đồ đặt trong khung vừa dựng — đo lại kích thước sau khi trình duyệt dàn trang xong.
  setTimeout(() => {
    if (host.isConnected) map.invalidateSize();
  }, 0);

  return {
    focus(plotId) {
      const shape = layers.get(plotId);
      if (!shape) return;
      fit(shape.getBounds());
      shape.openPopup();
      mapEl.scrollIntoView({ block: 'center' });
    },
    destroy: () => map.remove(),
  };
}
